import React, { useEffect, useMemo, useState } from "react";
import { superadminAPI } from "../../../api/superadminClient";
import {
  DEFAULT_BASE_PRICE,
  DEFAULT_DISCOUNT_BY_MONTH,
  formatInr,
} from "./shared";

// Cycles a negotiated contract can be written against. Deliberately narrower
// than the global discount grid: deals are quoted monthly, quarterly or yearly.
const DEAL_CYCLES = [
  { months: 1, label: "Monthly" },
  { months: 3, label: "Quarterly" },
  { months: 12, label: "Yearly" },
];

const GLOBAL_DURATIONS = [1, 3, 6, 12];

const EMPTY_NEW_TENANT = {
  companyName: "",
  adminFirstName: "",
  adminLastName: "",
  adminEmail: "",
  module: "dms",
  seats: "",
  expiresOn: "",
};

export default function PricingPanel({ onError, onTenantCreated }) {
  // --- global defaults ---
  const [discounts, setDiscounts] = useState(DEFAULT_DISCOUNT_BY_MONTH);
  const [basePrice, setBasePrice] = useState(DEFAULT_BASE_PRICE);
  const [sampleUsers, setSampleUsers] = useState(25);
  const [savingDiscounts, setSavingDiscounts] = useState(false);

  // --- per-tenant deals ---
  const [tenants, setTenants] = useState([]);
  const [selectedTenantId, setSelectedTenantId] = useState("");
  const [deal, setDeal] = useState(null);
  const [dealDraft, setDealDraft] = useState({
    flatAmounts: {},
    includedUsers: "",
    note: "",
  });
  const [dealLoading, setDealLoading] = useState(false);
  const [savingDeal, setSavingDeal] = useState(false);
  const [dealMessage, setDealMessage] = useState("");

  // --- pre-provision ---
  const [showCreate, setShowCreate] = useState(false);
  const [newTenant, setNewTenant] = useState(EMPTY_NEW_TENANT);
  const [newDeal, setNewDeal] = useState({
    flatAmounts: {},
    includedUsers: "",
    note: "",
  });
  const [creating, setCreating] = useState(false);
  const [createResult, setCreateResult] = useState(null);

  const loadGlobals = async () => {
    try {
      const resp = await superadminAPI.getDiscounts();
      const mapped = { ...DEFAULT_DISCOUNT_BY_MONTH };
      (resp.data?.discounts || []).forEach((row) => {
        if (row.planType === "PRO") {
          mapped[row.durationMonths] = Number(row.discountPercent) || 0;
        }
      });
      setDiscounts(mapped);
      setBasePrice(
        Number(resp.data?.proBasePricePerUserMonth) || DEFAULT_BASE_PRICE,
      );
    } catch (err) {
      onError(err.response?.data?.message || "Failed to load pricing config");
    }
  };

  const loadTenants = async () => {
    try {
      const resp = await superadminAPI.getTenants();
      setTenants(resp.data || []);
    } catch (err) {
      onError(err.response?.data?.message || "Failed to load tenants");
    }
  };

  useEffect(() => {
    loadGlobals();
    loadTenants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pricingPreview = useMemo(() => {
    const users = Math.max(1, Number(sampleUsers) || 1);
    return GLOBAL_DURATIONS.map((months) => {
      const discount = Math.max(
        0,
        Math.min(100, Number(discounts[months]) || 0),
      );
      const gross = users * (Number(basePrice) || 0) * months;
      const discountAmount = Math.round((gross * discount) / 100);
      return {
        months,
        users,
        discount,
        gross,
        discountAmount,
        total: gross - discountAmount,
      };
    });
  }, [sampleUsers, discounts, basePrice]);

  const handleSaveDiscounts = async () => {
    setSavingDiscounts(true);
    onError("");
    try {
      const payload = GLOBAL_DURATIONS.map((months) => ({
        planType: "PRO",
        durationMonths: months,
        discountPercent: Number(discounts[months]) || 0,
        isActive: true,
      }));
      await superadminAPI.saveDiscounts(payload, Number(basePrice) || undefined);
      await loadGlobals();
    } catch (err) {
      onError(err.response?.data?.message || "Failed to save pricing");
    } finally {
      setSavingDiscounts(false);
    }
  };

  const loadDeal = async (tenantId) => {
    if (!tenantId) {
      setDeal(null);
      return;
    }
    setDealLoading(true);
    setDealMessage("");
    try {
      const resp = await superadminAPI.getTenantPricing(tenantId);
      setDeal(resp.data);
      setDealDraft({
        flatAmounts: resp.data?.flatAmounts || {},
        includedUsers: resp.data?.includedUsers ?? "",
        note: resp.data?.note ?? "",
      });
    } catch (err) {
      onError(err.response?.data?.message || "Failed to load tenant pricing");
    } finally {
      setDealLoading(false);
    }
  };

  const handleSelectTenant = (tenantId) => {
    setSelectedTenantId(tenantId);
    loadDeal(tenantId);
  };

  const handleSaveDeal = async () => {
    if (!selectedTenantId) return;
    setSavingDeal(true);
    setDealMessage("");
    onError("");
    try {
      const resp = await superadminAPI.saveTenantPricing(selectedTenantId, {
        flatAmounts: dealDraft.flatAmounts,
        includedUsers: dealDraft.includedUsers === "" ? null : dealDraft.includedUsers,
        note: dealDraft.note,
        isActive: true,
      });
      setDeal(resp.data);
      setDealMessage("Contract saved. It applies from their next payment.");
    } catch (err) {
      onError(err.response?.data?.message || "Failed to save contract");
    } finally {
      setSavingDeal(false);
    }
  };

  const handleClearDeal = async () => {
    if (!selectedTenantId) return;
    if (
      !window.confirm(
        "Remove this contract? The tenant reverts to standard pricing at their next renewal.",
      )
    )
      return;
    setSavingDeal(true);
    onError("");
    try {
      const resp = await superadminAPI.clearTenantPricing(selectedTenantId);
      setDeal(resp.data);
      setDealDraft({ flatAmounts: {}, includedUsers: "", note: "" });
      setDealMessage("Contract removed. Standard pricing now applies.");
    } catch (err) {
      onError(err.response?.data?.message || "Failed to remove contract");
    } finally {
      setSavingDeal(false);
    }
  };

  const handleCreateTenant = async () => {
    if (
      !newTenant.companyName ||
      !newTenant.adminFirstName ||
      !newTenant.adminLastName ||
      !newTenant.adminEmail
    ) {
      onError("Company name, admin name and admin email are all required");
      return;
    }
    if (!newTenant.seats || Number(newTenant.seats) < 1) {
      onError("Enter the number of seats this account is licensed for");
      return;
    }
    if (newTenant.expiresOn && new Date(newTenant.expiresOn) <= new Date()) {
      onError("Access expiry must be a future date");
      return;
    }
    setCreating(true);
    setCreateResult(null);
    onError("");
    try {
      const resp = await superadminAPI.createTenant({
        ...newTenant,
        seats: newTenant.seats === "" ? null : Number(newTenant.seats),
        expiresOn: newTenant.expiresOn || null,
        flatAmounts: newDeal.flatAmounts,
        note: newDeal.note,
      });
      setCreateResult(resp.data);
      setNewTenant(EMPTY_NEW_TENANT);
      setNewDeal({ flatAmounts: {}, includedUsers: "", note: "" });
      await loadTenants();
      if (onTenantCreated) onTenantCreated();
    } catch (err) {
      onError(
        err.response?.data?.error ||
          err.response?.data?.message ||
          "Failed to create tenant",
      );
    } finally {
      setCreating(false);
    }
  };

  const dealCycleInputs = (draft, setDraft, { showSeatCap = true } = {}) => (
    <div className="superadmin-discount-controls">
      {DEAL_CYCLES.map(({ months, label }) => (
        <label key={months}>
          {label} (₹ total)
          <input
            type="number"
            min={0}
            placeholder="standard"
            value={draft.flatAmounts[months] ?? ""}
            onChange={(e) => {
              const value = e.target.value;
              setDraft((prev) => {
                const next = { ...prev.flatAmounts };
                if (value === "") delete next[months];
                else next[months] = Number(value);
                return { ...prev, flatAmounts: next };
              });
            }}
          />
        </label>
      ))}
      {showSeatCap && (
        <label>
          Seat Cap
          <input
            type="number"
            min={1}
            placeholder="uncapped"
            value={draft.includedUsers}
            onChange={(e) =>
              setDraft((prev) => ({ ...prev, includedUsers: e.target.value }))
            }
          />
        </label>
      )}
      <label style={{ flex: "1 1 260px" }}>
        Contract Note
        <input
          type="text"
          placeholder="e.g. Signed Aug 2026, 2-year term"
          value={draft.note}
          onChange={(e) =>
            setDraft((prev) => ({ ...prev, note: e.target.value }))
          }
        />
      </label>
    </div>
  );

  const activeCycles = Object.keys(dealDraft.flatAmounts)
    .map(Number)
    .sort((a, b) => a - b);

  return (
    <>
      <section className="superadmin-panel">
        <div className="superadmin-panel-head">
          <h2>Standard Pricing</h2>
          <p>
            The default every tenant gets. Applied on the public registration
            page and to any tenant without a negotiated contract.
          </p>
        </div>
        <div className="superadmin-discount-controls">
          <label>
            Sample User Count
            <input
              type="number"
              min={1}
              value={sampleUsers}
              onChange={(e) => setSampleUsers(e.target.value)}
            />
          </label>
          <label>
            Base Price/User/Month
            <input
              type="number"
              min={1}
              value={basePrice}
              onChange={(e) => setBasePrice(e.target.value)}
            />
          </label>
        </div>

        <div className="superadmin-table-wrap">
          <table className="superadmin-table">
            <thead>
              <tr>
                <th>Duration</th>
                <th>Discount %</th>
                <th>Gross</th>
                <th>Discount Value</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {pricingPreview.map((row) => (
                <tr key={row.months}>
                  <td>{row.months} Month(s)</td>
                  <td>
                    <input
                      type="number"
                      min={0}
                      max={100}
                      value={discounts[row.months]}
                      onChange={(e) =>
                        setDiscounts((prev) => ({
                          ...prev,
                          [row.months]: e.target.value,
                        }))
                      }
                    />
                  </td>
                  <td>{formatInr(row.gross)}</td>
                  <td>-{formatInr(row.discountAmount)}</td>
                  <td>{formatInr(row.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <button onClick={handleSaveDiscounts} disabled={savingDiscounts}>
          {savingDiscounts ? "Saving..." : "Save Standard Pricing"}
        </button>
      </section>

      <section className="superadmin-panel">
        <div className="superadmin-panel-head">
          <h2>Negotiated Contracts</h2>
          <p>
            Set a flat price per billing cycle for one customer. A contracted
            tenant is charged that amount regardless of seat count, and can only
            buy the cycles you price here.
          </p>
        </div>

        <div className="superadmin-discount-controls">
          <label style={{ flex: "1 1 320px" }}>
            Customer
            <select
              value={selectedTenantId}
              onChange={(e) => handleSelectTenant(e.target.value)}
            >
              <option value="">Select a tenant...</option>
              {tenants.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </label>
        </div>

        {dealLoading && <div className="superadmin-loading">Loading contract...</div>}

        {selectedTenantId && !dealLoading && (
          <>
            <p style={{ color: deal?.hasDeal ? "#166534" : "#64748b", margin: "8px 0" }}>
              {deal?.hasDeal
                ? `On a negotiated contract${
                    deal.includedUsers ? ` capped at ${deal.includedUsers} users` : ""
                  }.`
                : "Currently on standard pricing. Enter an amount for any cycle to put them on contract."}
            </p>

            {dealCycleInputs(dealDraft, setDealDraft)}

            <p style={{ color: "#64748b", fontSize: 13, margin: "8px 0" }}>
              {activeCycles.length
                ? `They will be able to buy: ${activeCycles
                    .map((m) => DEAL_CYCLES.find((c) => c.months === m)?.label || `${m}mo`)
                    .join(", ")}. Leave a cycle blank to withdraw it.`
                : "No cycles priced — saving with all blank leaves them on standard pricing."}
            </p>

            {dealMessage && (
              <div style={{ color: "#166534", margin: "8px 0" }}>{dealMessage}</div>
            )}

            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={handleSaveDeal} disabled={savingDeal}>
                {savingDeal ? "Saving..." : "Save Contract"}
              </button>
              {deal?.hasDeal && (
                <button
                  onClick={handleClearDeal}
                  disabled={savingDeal}
                  className="superadmin-btn-danger"
                >
                  Remove Contract
                </button>
              )}
            </div>
          </>
        )}
      </section>

      <section className="superadmin-panel">
        <div className="superadmin-panel-head">
          <h2>Pre-provision a Customer</h2>
          <p>
            Create an account that starts on its negotiated contract, so the
            customer never sees standard pricing. They get an email invite to set
            a password, and the app stays locked until they pay.
          </p>
        </div>

        {!showCreate ? (
          <button onClick={() => setShowCreate(true)}>New Contracted Customer</button>
        ) : (
          <>
            <div className="superadmin-discount-controls">
              <label style={{ flex: "1 1 260px" }}>
                Company Name
                <input
                  type="text"
                  value={newTenant.companyName}
                  onChange={(e) =>
                    setNewTenant((p) => ({ ...p, companyName: e.target.value }))
                  }
                />
              </label>
              <label>
                Admin First Name
                <input
                  type="text"
                  value={newTenant.adminFirstName}
                  onChange={(e) =>
                    setNewTenant((p) => ({ ...p, adminFirstName: e.target.value }))
                  }
                />
              </label>
              <label>
                Admin Last Name
                <input
                  type="text"
                  value={newTenant.adminLastName}
                  onChange={(e) =>
                    setNewTenant((p) => ({ ...p, adminLastName: e.target.value }))
                  }
                />
              </label>
              <label style={{ flex: "1 1 260px" }}>
                Admin Email
                <input
                  type="email"
                  value={newTenant.adminEmail}
                  onChange={(e) =>
                    setNewTenant((p) => ({ ...p, adminEmail: e.target.value }))
                  }
                />
              </label>
              <label>
                Module
                <select
                  value={newTenant.module}
                  onChange={(e) =>
                    setNewTenant((p) => ({ ...p, module: e.target.value }))
                  }
                >
                  <option value="dms">Document / Project (dms)</option>
                  <option value="factory">Manufacturing (factory)</option>
                </select>
              </label>
              <label>
                Seats
                <input
                  type="number"
                  min={1}
                  placeholder="e.g. 30"
                  value={newTenant.seats}
                  onChange={(e) =>
                    setNewTenant((p) => ({ ...p, seats: e.target.value }))
                  }
                />
              </label>
              <label>
                Access Expires On
                <input
                  type="date"
                  value={newTenant.expiresOn}
                  onChange={(e) =>
                    setNewTenant((p) => ({ ...p, expiresOn: e.target.value }))
                  }
                />
              </label>
            </div>

            <p style={{ color: "#64748b", fontSize: 13, margin: "4px 0 0" }}>
              {newTenant.expiresOn
                ? "Account will be active immediately until this date — use this when the contract is invoiced offline."
                : "Leave the expiry blank to keep the account locked until they pay online."}
            </p>

            <h4 style={{ margin: "12px 0 4px", color: "#0f2d5c" }}>Contract Terms</h4>
            {dealCycleInputs(newDeal, setNewDeal, { showSeatCap: false })}

            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <button onClick={handleCreateTenant} disabled={creating}>
                {creating ? "Creating..." : "Create & Send Invite"}
              </button>
              <button
                onClick={() => {
                  setShowCreate(false);
                  setNewTenant(EMPTY_NEW_TENANT);
                  setNewDeal({ flatAmounts: {}, includedUsers: "", note: "" });
                }}
                disabled={creating}
              >
                Cancel
              </button>
            </div>
          </>
        )}

        {createResult && (
          <div style={{ color: "#166534", marginTop: 12 }}>
            Created <strong>{createResult.tenant?.name}</strong> with{" "}
            {createResult.seats} seats — invite sent to {createResult.adminEmail}.{" "}
            {createResult.expiresOn
              ? `Active until ${new Date(createResult.expiresOn).toLocaleDateString("en-IN")}.`
              : "Locked until first payment."}
          </div>
        )}
      </section>
    </>
  );
}
